import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  amenitiesList,
  propertyTypes,
  ListingIntent,
  PropertyProgressStatus
} from '../property/property-card/PropertyCardTypes';
import { ManagedProperty } from '../property/property-card/PropertyCardTypes';

export type ManagedPropertyFormState = {
  address: string;
  type: ManagedProperty['type'];
  bedrooms: number;
  bathrooms: number;
  size?: number;
  berRating: string;
  amenities: string[];
  notes: string;
  // New fields
  status: 'managed' | 'managed-archived';
  listingIntent: ListingIntent;
  propertyProgressStatus?: PropertyProgressStatus;
  archiveReason?: string;
};

interface EditManagedPropertyModalProps {
  isOpen: boolean;
  property: ManagedProperty;
  form: ManagedPropertyFormState;
  savingChanges: boolean;
  onClose: () => void;
  onChange: (field: keyof ManagedPropertyFormState, value: any) => void;
  onToggleAmenity: (amenity: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const EditManagedPropertyModal: React.FC<EditManagedPropertyModalProps> = ({
  isOpen,
  property,
  form,
  savingChanges,
  onClose,
  onChange,
  onToggleAmenity,
  onSubmit
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8">
      <div className="w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <p className="text-sm font-semibold text-blue-500">
              Edit Managed Property
            </p>
            <h3 className="text-2xl font-bold text-gray-900">
              {property.title || property.type}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 transition hover:text-gray-700"
            aria-label="Close edit modal"
          >
            ×
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          className="max-h-[70vh] overflow-y-auto px-6 py-6"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-gray-700">
              Address
              <input
                type="text"
                value={form.address}
                onChange={(e) => onChange('address', e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                required
              />
            </label>

            <label className="text-sm font-semibold text-gray-700">
              Property Type
              <select
                value={form.type}
                onChange={(e) => onChange('type', e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                {propertyTypes
                  .filter((type: string) => type !== 'Any')
                  .map((type: string) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
              </select>
            </label>

            <label className="text-sm font-semibold text-gray-700">
              Bedrooms
              <input
                type="number"
                min={0}
                max={20}
                value={form.bedrooms}
                onChange={(e) => onChange('bedrooms', Number(e.target.value))}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>

            <label className="text-sm font-semibold text-gray-700">
              Bathrooms
              <input
                type="number"
                min={0}
                max={10}
                value={form.bathrooms}
                onChange={(e) => onChange('bathrooms', Number(e.target.value))}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>

            <label className="text-sm font-semibold text-gray-700">
              Size (sqm)
              <input
                type="number"
                min={0}
                value={form.size ?? ''}
                onChange={(e) => onChange('size', Number(e.target.value))}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>

            <label className="text-sm font-semibold text-gray-700">
              BER Rating
              <input
                type="text"
                value={form.berRating}
                onChange={(e) => onChange('berRating', e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>

            <label className="text-sm font-semibold text-gray-700">
              Listing Intent
              <select
                value={form.listingIntent}
                onChange={(e) =>
                  onChange('listingIntent', e.target.value as ListingIntent)
                }
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                {(['rent', 'sale', 'share'] as ListingIntent[]).map(
                  (intent) => (
                    <option key={intent} value={intent}>
                      {intent.charAt(0).toUpperCase() + intent.slice(1)}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="text-sm font-semibold text-gray-700">
              Progress Status
              <select
                value={form.propertyProgressStatus ?? ''}
                onChange={(e) =>
                  onChange(
                    'propertyProgressStatus',
                    (e.target.value || undefined) as
                      | PropertyProgressStatus
                      | undefined
                  )
                }
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="">None</option>
                {(
                  [
                    'new',
                    'viewing-in-progress',
                    'sale-agreed'
                  ] as PropertyProgressStatus[]
                ).map((status) => (
                  <option key={status} value={status}>
                    {status === 'new'
                      ? 'New'
                      : status === 'viewing-in-progress'
                        ? 'Viewing In Progress'
                        : 'Sale Agreed'}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm font-semibold text-gray-700">
              Property Status
              <select
                value={form.status}
                onChange={(e) =>
                  onChange(
                    'status',
                    e.target.value as 'managed' | 'managed-archived'
                  )
                }
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="managed">Active</option>
                <option value="managed-archived">Archived</option>
              </select>
            </label>

            {form.status === 'managed-archived' && (
              <label className="text-sm font-semibold text-gray-700 md:col-span-2">
                Archive Reason (optional)
                <textarea
                  value={form.archiveReason ?? ''}
                  onChange={(e) => onChange('archiveReason', e.target.value)}
                  rows={3}
                  placeholder="e.g., Property sold, no longer rented"
                  className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-100"
                />
              </label>
            )}
          </div>

          <div className="mt-4">
            <p className="text-sm font-semibold text-gray-700">Amenities</p>
            <div className="mt-2 grid gap-2 md:grid-cols-3">
              {amenitiesList.map((amenity: string) => (
                <label
                  key={amenity}
                  className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 transition hover:border-blue-300"
                >
                  <input
                    type="checkbox"
                    checked={form.amenities.includes(amenity)}
                    onChange={() => onToggleAmenity(amenity)}
                  />
                  {amenity}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-gray-700">
              Private Notes
              <textarea
                value={form.notes}
                onChange={(e) => onChange('notes', e.target.value)}
                rows={4}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/40 transition hover:scale-[1.01]"
              disabled={savingChanges}
            >
              {savingChanges && (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              )}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditManagedPropertyModal;
